import React, { useState, useRef, useEffect } from 'react';
import { useQCM } from '../../contexts/QCMContext';
import { useTranslation } from 'react-i18next';
import { beTheTeacherApiStreaming } from '../../services/api';

const BeTheTeacher = () => {
    const { t } = useTranslation();
    const {
        currentQCM,
        sessionContext,
        loading
    } = useQCM();

    const [teacherAnswer, setTeacherAnswer] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [showInput, setShowInput] = useState(false);
    const [response, setResponse] = useState('');
    const [score, setScore] = useState(null);
    const [topics, setTopics] = useState([]);
    const [encouragement, setEncouragement] = useState('');
    const textAreaRef = useRef(null);
    const responseRef = useRef('');

    // Character limit for teacher answer (7 lines max)
    const MAX_ANSWER_LENGTH = 500; // Approximate for 7 lines

    useEffect(() => {
        if (showInput && textAreaRef.current) {
            textAreaRef.current.focus();
        }
    }, [showInput]);

    const handleAnswerChange = (e) => {
        const value = e.target.value;
        if (value.length <= MAX_ANSWER_LENGTH) {
            setTeacherAnswer(value);
        }
    };

    const handleSubmit = async () => {
        if (!teacherAnswer.trim() || isLoading) return;

        setIsLoading(true);
        setResponse('');
        setScore(null);
        setTopics([]);
        setEncouragement('');

        try {
            // Prepare the payload for beTheTeacher evaluation using open-question API
            const payload = {
                // From current QCM displayed (with fallbacks)
                Domain: currentQCM?.metadata?.Domain || 'General',
                topic: currentQCM?.metadata?.topic || 'General Topic',
                'Topic_Occurrence_%': currentQCM?.metadata?.['Topic_Occurrence_%'] || 0,
                question: currentQCM?.qcm?.question || currentQCM?.question || 'General context question',
                
                // Priority set in user context (default to 1 as per spec)
                priority: sessionContext?.priority || 1,
                
                // Subject to answer (user input) - this will be the teacher's explanation
                subject_to_answer: teacherAnswer.trim(),
                
                // Exam name and editor solution from context (with fallbacks)
                exam_name: currentQCM?.exam_title || sessionContext?.exam || 'General Learning',
                editor_solution: currentQCM?.editor_solution || sessionContext?.editor_solution || 'Learning Context',
                
                // History of previous questions
                history: [],

                // Additional context fields
                user: sessionContext?.user || 'learner1',
                sessionId: sessionContext?.sessionId || '111222',
                exam_id: sessionContext?.exam || 'general-learning'
            };

                                console.log('Sending beTheTeacher payload via dedicated API:', payload);
                    
                    // Use dedicated beTheTeacher streaming API
                    await beTheTeacherApiStreaming(
                payload,
                // onMessage - handle streaming chunks
                (data) => {
                    console.log('BeTheTeacher onMessage - data:', data);
                    if (data.type === 'status') {
                        setResponse(data.message || 'Processing your explanation...');
                    } else if (data.type === 'chunk') {
                        setResponse(prev => {
                            const newResponse = prev + data.text + ' ';
                            responseRef.current = newResponse;
                            return newResponse;
                        });
                    }
                },
                // onError - handle errors
                (error) => {
                    console.error('BeTheTeacher streaming error:', error);
                    setResponse('Error processing your explanation. Please try again.');
                    setIsLoading(false);
                },
                // onComplete - handle completion
                (finalData) => {
                    console.log('BeTheTeacher onComplete - finalData:', finalData);
                    
                    if (finalData?.success) {
                        // Process the response to extract score, topics, and encouragement
                        processResponse(responseRef.current);
                    } else {
                        setResponse('Error processing your explanation. Please try again.');
                    }
                    setIsLoading(false);
                }
            );

        } catch (error) {
            console.error('Error submitting beTheTeacher answer:', error);
            setResponse('Error processing your explanation. Please try again.');
            setIsLoading(false);
        }
    };

    const processResponse = (responseText) => {
        console.log('Processing BeTheTeacher response:', responseText);
        
        // Extract score (first number in the response, as per prompt template)
        const scoreMatch = responseText.match(/^(\d+(?:\.\d+)?)/);
        if (scoreMatch) {
            const extractedScore = parseFloat(scoreMatch[1]);
            setScore(extractedScore);
            
            // Set encouragement based on score
            if (extractedScore >= 7.5) {
                setEncouragement('Excellent work! You have a solid understanding of this topic.');
            } else if (extractedScore >= 5) {
                setEncouragement('Good effort! You\'re on the right track, keep practicing.');
            } else {
                setEncouragement('Keep learning! Every attempt helps you improve.');
            }
        }

        // Extract topics (text between **Topic** but not **Room for improvement**)
        const topicMatches = responseText.match(/\*\*([^*]+)\*\*/g);
        if (topicMatches) {
            const extractedTopics = topicMatches
                .map(topic => topic.replace(/\*\*/g, '').trim())
                .filter(topic => 
                    topic.toLowerCase() !== 'room for improvement' && 
                    topic.toLowerCase() !== 'purpose' &&
                    topic.toLowerCase() !== 'use case' &&
                    topic.toLowerCase() !== 'analogy' &&
                    topic.toLowerCase() !== 'mnemonic' &&
                    topic.toLowerCase() !== 'common errors'
                );
            setTopics(extractedTopics);
        }
    };

    const formatResponse = (text) => {
        if (!text) return '';
        
        // Convert **text** to <strong>text</strong>
        return text.split('\n').map((paragraph, index) => {
            const formattedParagraph = paragraph.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>');
            return (
                <p 
                    key={index} 
                    className="mb-2 last:mb-0"
                    dangerouslySetInnerHTML={{ __html: formattedParagraph }}
                />
            );
        });
    };

    const getScoreColor = (score) => {
        if (score >= 7.5) return 'text-green-400';
        if (score >= 5) return 'text-yellow-400';
        return 'text-red-400';
    };

    const getScoreBgColor = (score) => {
        if (score >= 7.5) return 'bg-green-900/20 border-green-500';
        if (score >= 5) return 'bg-yellow-900/20 border-yellow-500';
        return 'bg-red-900/20 border-red-500';
    };

    if (!currentQCM) {
        return null;
    }

    return (
        <div className="space-y-6">
            <div className="text-center mb-6">
                <h3 className="text-xl font-semibold text-purple-400 mb-2 flex items-center justify-center">
                    <svg className="w-6 h-6 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
                    </svg>
                    Be the Teacher
                </h3>
                <p className="text-gray-400 text-sm">
                    Explain the topic as if you were teaching it to someone else
                </p>
            </div>

            {/* Input Section */}
            {!showInput && (
                <div className="text-center">
                    <button
                        onClick={() => setShowInput(true)}
                        className="bg-purple-600 hover:bg-purple-700 text-white px-6 py-3 rounded-lg font-medium transition-colors"
                    >
                        Start Teaching
                    </button>
                </div>
            )}

            {showInput && (
                <div className="space-y-4">
                    <div>
                        <label className="block text-sm font-medium text-gray-300 mb-2">
                            Your explanation (max 7 lines):
                        </label>
                        <textarea
                            ref={textAreaRef}
                            value={teacherAnswer}
                            onChange={handleAnswerChange}
                            placeholder="Explain this topic as if you were teaching it to a student..."
                            className="w-full h-32 bg-gray-700 text-white text-base border border-gray-600 rounded px-3 py-2 focus:outline-none focus:border-purple-500 resize-none"
                            disabled={isLoading}
                            maxLength={MAX_ANSWER_LENGTH}
                        />
                        <div className="text-xs text-gray-500 mt-1">
                            {teacherAnswer.length} / {MAX_ANSWER_LENGTH} characters
                        </div>
                    </div>

                    <div className="flex justify-center">
                        <button
                            onClick={handleSubmit}
                            disabled={!teacherAnswer.trim() || isLoading}
                            className="bg-purple-600 hover:bg-purple-700 disabled:bg-gray-600 text-white px-6 py-2 rounded-lg font-medium transition-colors"
                        >
                            {isLoading ? 'Evaluating...' : 'Submit Explanation'}
                        </button>
                    </div>
                </div>
            )}

            {/* Response Section */}
            {(response || score !== null) && (
                <div className="space-y-4">
                    {/* Score Display */}
                    {score !== null && (
                        <div className={`p-4 rounded-lg border ${getScoreBgColor(score)}`}>
                            <div className="flex items-center justify-between">
                                <div>
                                    <h4 className="text-lg font-semibold text-white">Your Score</h4>
                                    <p className={`text-2xl font-bold ${getScoreColor(score)}`}>
                                        {score}/10
                                    </p>
                                </div>
                                <div className="text-right">
                                    <p className="text-sm text-gray-300">
                                        {score >= 7.5 ? 'Success!' : score >= 5 ? 'Good effort!' : 'Keep learning!'}
                                    </p>
                                </div>
                            </div>
                            {encouragement && (
                                <p className="text-sm text-gray-300 mt-2">{encouragement}</p>
                            )}
                        </div>
                    )}

                    {/* Topics */}
                    {topics.length > 0 && (
                        <div className="bg-gray-800 rounded-lg p-4">
                            <h4 className="text-lg font-semibold text-blue-400 mb-3">Topics Covered</h4>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                                {topics.map((topic, index) => (
                                    <div key={index} className="bg-gray-700 rounded p-2">
                                        <span className="text-blue-300 font-medium">{topic}</span>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}

                    {/* Full Response */}
                    {response && (
                        <div className="bg-gray-800 rounded-lg p-4">
                            <h4 className="text-lg font-semibold text-purple-400 mb-3 text-center">Detailed Feedback</h4>
                            <div className="text-gray-300 leading-relaxed">
                                {formatResponse(response)}
                            </div>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

export default BeTheTeacher;
